import { useState, useEffect } from 'react'
import { dataService } from '../services/dataService'
import { Plus, FileText, Receipt, Trash2, WifiOff, Tag, EyeOff, Eye } from 'lucide-react'
import ClienteDialog from './ClienteDialog'
import AlbaranesClienteDialog from './AlbaranesClienteDialog'
import FacturasClienteDialog from './FacturasClienteDialog'
import PreciosCliente from './PreciosCliente'

export default function Clientes({ usuario }) {
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [clienteEditar, setClienteEditar] = useState(null)
  const [mostrarDialog, setMostrarDialog] = useState(false)
  const [clienteAlbaranes, setClienteAlbaranes] = useState(null)
  const [clienteFacturas, setClienteFacturas] = useState(null)
  const [clientePrecios, setClientePrecios] = useState(null)
  const [dataSource, setDataSource] = useState('online')
  const [mostrarInactivos, setMostrarInactivos] = useState(false)

  useEffect(() => {
    cargarClientes()
  }, [])

  async function cargarClientes() {
    try {
      const clientesRes = await dataService.getClients(false) // traer todos, filtramos en el componente
      setDataSource(clientesRes.source)
      const clientesOrdenados = (clientesRes.data || []).sort((a, b) => {
        if (a.activo !== b.activo) return b.activo - a.activo
        return a.alias.localeCompare(b.alias)
      })
      setClientes(clientesOrdenados)
    } catch (error) {
      console.error('Error cargando clientes:', error)
      alert('Error al cargar clientes')
    } finally {
      setLoading(false)
    }
  }

  function abrirNuevoCliente() {
    setClienteEditar(null)
    setMostrarDialog(true)
  }

  function abrirEditarCliente(cliente) {
    setClienteEditar(cliente)
    setMostrarDialog(true)
  }

  async function eliminarCliente(id) {
    if (!confirm('¿Marcar este cliente como inactivo?\n\nPodrás volver a verlo activando "Mostrar inactivos".')) return
    try {
      const result = await dataService.deleteClient(id)
      if (result.success) {
        const mensaje = result.mode === 'offline'
          ? 'Cliente marcado como inactivo OFFLINE. Se sincronizará cuando haya conexión.'
          : 'Cliente marcado como inactivo correctamente.'
        alert(mensaje)
        cargarClientes()
      }
    } catch (error) {
      alert('❌ Error: ' + error.message)
    }
  }

  const esAdmin = usuario?.rol === 'admin'
  const clientesFiltrados = mostrarInactivos ? clientes : clientes.filter(c => c.activo)
  const numInactivos = clientes.filter(c => !c.activo).length

  return (
    <div>
      {dataSource === 'cache' && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid var(--warn)',
          borderRadius: '6px',
          fontSize: '0.875rem',
          color: 'var(--warn)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <WifiOff size={18} />
          <span>📵 Modo offline - Puedes crear/editar/eliminar. Se sincronizará al conectar.</span>
        </div>
      )}

      <div className="seccion-header">
        <h2>Gestión de Clientes</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {numInactivos > 0 && (
            <button
              onClick={() => setMostrarInactivos(v => !v)}
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
            >
              {mostrarInactivos ? <Eye size={16} /> : <EyeOff size={16} />}
              {mostrarInactivos ? 'Ocultar inactivos' : `Inactivos (${numInactivos})`}
            </button>
          )}
          <button onClick={abrirNuevoCliente} className="btn-primary">
            <Plus size={20} /> Nuevo cliente
          </button>
        </div>
      </div>

      <div className="tabla-container">
        <div className="tabla-scroll">
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              Cargando clientes...
            </div>
          ) : clientesFiltrados.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              No hay clientes registrados
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th style={{ width: esAdmin ? '160px' : '100px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltrados.map(cliente => (
                  <tr
                    key={cliente.id}
                    onClick={() => abrirEditarCliente(cliente)}
                    style={{ cursor: 'pointer', opacity: cliente.activo ? 1 : 0.5 }}
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>{cliente.alias}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.15rem' }}>
                        {cliente.razon_social}
                        {cliente.ruta ? <span style={{ marginLeft: '0.5rem' }}>· {cliente.ruta}</span> : null}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${cliente.activo ? 'badge-success' : 'badge-danger'}`}
                        style={!cliente.activo ? {
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: 'var(--warn)',
                          border: '1px solid var(--warn)'
                        } : {}}
                      >
                        {cliente.activo ? '✓ Activo' : '✗ Inactivo'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="acciones-cell">
                        <button
                          onClick={() => setClienteAlbaranes(cliente)}
                          className="btn-icon"
                          title="Ver albaranes"
                          disabled={!navigator.onLine}
                          style={{ opacity: !navigator.onLine ? 0.5 : 1 }}
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => setClienteFacturas(cliente)}
                          className="btn-icon"
                          title="Ver facturas"
                          disabled={!navigator.onLine}
                          style={{ opacity: !navigator.onLine ? 0.5 : 1 }}
                        >
                          <Receipt size={16} />
                        </button>
                        {esAdmin && (
                          <>
                            <button
                              onClick={() => setClientePrecios(cliente)}
                              className="btn-icon"
                              title="Precios especiales"
                              disabled={!navigator.onLine}
                              style={{ opacity: !navigator.onLine ? 0.5 : 1, color: 'var(--accent)' }}
                            >
                              <Tag size={16} />
                            </button>
                            <button
                              onClick={() => eliminarCliente(cliente.id)}
                              className="btn-icon-danger"
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {mostrarDialog && (
        <ClienteDialog
          cliente={clienteEditar}
          onClose={() => { setMostrarDialog(false); setClienteEditar(null) }}
          onGuardar={() => { setMostrarDialog(false); setClienteEditar(null); cargarClientes() }}
        />
      )}
      {clienteAlbaranes && (
        <AlbaranesClienteDialog cliente={clienteAlbaranes} onClose={() => setClienteAlbaranes(null)} />
      )}
      {clienteFacturas && (
        <FacturasClienteDialog cliente={clienteFacturas} onClose={() => setClienteFacturas(null)} />
      )}
      {clientePrecios && (
        <PreciosCliente cliente={clientePrecios} onClose={() => setClientePrecios(null)} />
      )}
    </div>
  )
}
